@if (assignment$ | async; as assignment) {
  @if (isLoading) {
    <div class="assignment-overview-loader">
      <div class="assignment-overview-loader-spinner"></div>
      <span>{{ "MESSAGES.LOADING" | translate }}</span>
    </div>
  }

  <div class="assignment-overview-page">
    <app-header [title]="assignment.title" [subtitle]="assignment.project.name">
      @if (assignment.project.imageUrl !== notFound) {
        <div
          header-title-after
          class="assignment-overview-header-cover assignment-overview-header-cover--inline"
        >
          <img [src]="assignment.project.imageUrl" alt="" />
        </div>
      } @else {
        <div
          header-title-after
          class="assignment-overview-header-cover assignment-overview-header-cover--fallback assignment-overview-header-cover--inline"
          [ngStyle]="{ backgroundColor: assignment.project.color }"
        >
          <span>{{ assignment.project.name | abbreviation }}</span>
        </div>
      }

      <div header-actions class="hidden lg:block">
        <app-navbar-profile-info />
      </div>
    </app-header>

    <div class="assignment-overview-content py-6 md:py-8">
      <div class="assignment-overview-toolbar">
        <app-back-button [fallbackUrl]="assignmentBackUrl" />

        @if (projectTagLabel) {
          <span class="assignment-overview-project-tag">{{ projectTagLabel }}</span>
        }
      </div>

      @if (assignment.isArchived) {
        <p class="assignment-overview-archived-note">
          {{ "MESSAGES.ARCHIVED_ASSIGNMENT" | translate }}
        </p>
      }

      <form
        class="assignment-overview-grid"
        [formGroup]="assignmentFormModel.form"
      >
        <section class="assignment-overview-main">
          <app-card-shell class="assignment-overview-summary-card" padding="md">
            <div class="assignment-overview-title-block">
              @if ((canEditAssignment$ | async) === true && !assignment.isArchived) {
                <app-dynamic-editable-field
                  [formControlName]="formModel.fieldNames.title"
                  [inputType]="'text'"
                  [inputClass]="'assignment-overview-title-input'"
                  [editorClass]="'assignment-overview-title-editor'"
                  (updatedInput)="onUpdateInput(formModel.fieldNames.title)"
                >
                  <ng-template #labelTpl>
                    <h2 class="assignment-overview-title">{{ assignment.title }}</h2>
                  </ng-template>
                </app-dynamic-editable-field>
              } @else {
                <h2 class="assignment-overview-title">{{ assignment.title }}</h2>
              }
            </div>

            <div class="assignment-overview-description">
              @if ((canEditAssignment$ | async) === true && !assignment.isArchived) {
                <app-dynamic-editable-field
                  layout="stacked"
                  [formControlName]="formModel.fieldNames.description"
                  inputType="textarea"
                  editorClass="assignment-create-editor"
                  actionButtonsPosition="top"
                  (updatedInput)="onUpdateInput(formModel.fieldNames.description)"
                >
                  <ng-template #labelTpl>
                    <div class="assignment-overview-section-label">
                      {{ "INPUTS.DESCRIPTION" | translate }}
                    </div>
                  </ng-template>
                  <ng-template #contentTpl>
                    @if (assignment.description) {
                      <div
                        class="assignment-overview-description-content"
                        [innerHTML]="sanitizedDescription$ | async"
                      ></div>
                    } @else {
                      <app-empty-content
                        [param]="emptyDescriptionParam[currentLang]"
                      ></app-empty-content>
                    }
                  </ng-template>
                </app-dynamic-editable-field>
              } @else {
                <div class="assignment-overview-section-label">
                  {{ "INPUTS.DESCRIPTION" | translate }}
                </div>

                @if (assignment.description) {
                  <div
                    class="assignment-overview-description-content"
                    [innerHTML]="sanitizedDescription$ | async"
                  ></div>
                } @else {
                  <app-empty-content
                    [param]="emptyDescriptionParam[currentLang]"
                  ></app-empty-content>
                }
              }
            </div>
          </app-card-shell>

          <app-card-shell class="assignment-overview-files-card" padding="md">
            <app-attachments
              [attachmentsPath]="attachmentsPath"
              [companyId]="companyId"
              [projectId]="projectId"
              [assignmentId]="assignment.id"
              [isReadonly]="
                assignment.isArchived || (canEditAssignment$ | async) === false
              "
            ></app-attachments>
          </app-card-shell>

          @if (projectEmployees$ | async; as employees) {
            <section class="assignment-overview-discussion-section">
              <app-card-shell class="assignment-overview-discussion-card" padding="md">
                <app-tabs
                  class="assignment-overview-tabs-shell"
                  [items]="discussionTabItems"
                  [activeId]="activeDiscussionTab"
                  (activeIdChange)="onDiscussionTabChange($event)"
                />

                <div class="assignment-overview-tab-panel">
                  @if (activeDiscussionTab === discussionTabs.conversation) {
                    <app-assignment-comments
                      [projectEmployees]="employees"
                      [isReadonly]="
                        assignment.isArchived || (canEditAssignment$ | async) === false
                      "
                    />
                  } @else {
                    <app-assignment-reports
                      [companyId]="companyId"
                      [projectId]="projectId"
                      [assignmentId]="assignment.id"
                      [isReadonly]="
                        assignment.isArchived || (canEditAssignment$ | async) === false
                      "
                    />
                  }
                </div>
              </app-card-shell>
            </section>
          }
        </section>

        <aside class="assignment-overview-sidebar">
          <app-card-shell class="assignment-overview-sidebar-group" padding="md">
            <div class="assignment-overview-sidebar-list">
              <div class="assignment-overview-sidebar-item">
                @if ((canEditAssignment$ | async) === true && !assignment.isArchived) {
                  <app-dynamic-editable-field
                    layout="stacked"
                    [formControlName]="assignmentFormModel.fieldNames.status"
                    inputType="select"
                    editorClass="assignment-overview-editor-full"
                    inputClass="assignment-overview-field-input"
                    [selectItems]="orderedStatuses"
                    enumName="ASSIGNMENT_STATUSES"
                    [enumObject]="assignmentStatuses"
                    (updatedInput)="onUpdateInput(assignmentFormModel.fieldNames.status)"
                  >
                    <ng-template #labelTpl>
                      <span class="assignment-overview-sidebar-label">
                        {{ "COMPONENTS.ASSIGNMENT_OVERVIEW.STATUS" | translate }}
                      </span>
                    </ng-template>
                    <ng-template #contentTpl>
                      <span
                        class="assignment-status-badge"
                        [ngClass]="
                          'status-' +
                          (assignment.status | enumToKebabCase: assignmentStatuses)
                        "
                      >
                        {{
                          "ASSIGNMENT_STATUSES." +
                            (assignment.status | enumKey: assignmentStatuses | uppercase)
                            | translate
                        }}
                      </span>
                    </ng-template>
                  </app-dynamic-editable-field>
                } @else {
                  <span class="assignment-overview-sidebar-label">
                    {{ "COMPONENTS.ASSIGNMENT_OVERVIEW.STATUS" | translate }}
                  </span>
                  <span
                    class="assignment-status-badge"
                    [ngClass]="
                      'status-' +
                      (assignment.status | enumToKebabCase: assignmentStatuses)
                    "
                  >
                    {{
                      "ASSIGNMENT_STATUSES." +
                        (assignment.status | enumKey: assignmentStatuses | uppercase)
                        | translate
                    }}
                  </span>
                }
              </div>

              <div class="assignment-overview-sidebar-item">
                @if ((canEditAssignment$ | async) === true && !assignment.isArchived) {
                  <app-dynamic-editable-field
                    layout="stacked"
                    [formControlName]="assignmentFormModel.fieldNames.priority"
                    inputType="select"
                    editorClass="assignment-overview-editor-full"
                    inputClass="assignment-overview-field-input"
                    [selectItems]="orderedPriorities"
                    enumName="ASSIGNMENT_PRIORITIES"
                    [enumObject]="assignmentPriorities"
                    (updatedInput)="onUpdateInput(assignmentFormModel.fieldNames.priority)"
                  >
                    <ng-template #labelTpl>
                      <span class="assignment-overview-sidebar-label">
                        {{ "COMPONENTS.ASSIGNMENT_OVERVIEW.PRIORITY" | translate }}
                      </span>
                    </ng-template>
                    <ng-template #contentTpl>
                      <span
                        class="assignment-priority-mark"
                        [ngClass]="assignmentPriorityBadgeClassMap[assignment.priority]"
                      >
                        <span class="assignment-priority-mark__dot"></span>
                        <span>
                          {{
                            "ASSIGNMENT_PRIORITIES." +
                              (assignment.priority
                                | enumKey: assignmentPriorities
                                | uppercase) | translate
                          }}
                        </span>
                      </span>
                    </ng-template>
                  </app-dynamic-editable-field>
                } @else {
                  <span class="assignment-overview-sidebar-label">
                    {{ "COMPONENTS.ASSIGNMENT_OVERVIEW.PRIORITY" | translate }}
                  </span>
                  <span
                    class="assignment-priority-mark"
                    [ngClass]="assignmentPriorityBadgeClassMap[assignment.priority]"
                  >
                    <span class="assignment-priority-mark__dot"></span>
                    <span>
                      {{
                        "ASSIGNMENT_PRIORITIES." +
                          (assignment.priority | enumKey: assignmentPriorities | uppercase)
                          | translate
                      }}
                    </span>
                  </span>
                }
              </div>
            </div>
          </app-card-shell>

          <app-card-shell class="assignment-overview-sidebar-group" padding="md">
            <div class="assignment-overview-sidebar-list">
              <div class="assignment-overview-sidebar-item">
                <span class="assignment-overview-sidebar-label">
                  {{ "ASSIGNMENT_INFO.CREATED" | translate }}
                </span>
                <span class="assignment-overview-sidebar-value">
                  {{ assignment.createdAt | date: uaDateWithTimeFormat }}
                </span>
              </div>

              <div class="assignment-overview-sidebar-item">
                <span class="assignment-overview-sidebar-label">
                  {{ "ASSIGNMENT_INFO.START_DATE" | translate }}
                </span>
                <span class="assignment-overview-sidebar-value">
                  {{ assignment.startDate | date: uaDateWithTimeFormat }}
                </span>
              </div>

              <div class="assignment-overview-sidebar-item">
                @if ((canEditAssignment$ | async) === true && !assignment.isArchived) {
                  <app-dynamic-editable-field
                    layout="stacked"
                    [formControlName]="assignmentFormModel.fieldNames.deadline"
                    inputType="date"
                    editorClass="assignment-overview-editor-full"
                    inputClass="assignment-overview-field-input"
                    (updatedInput)="onUpdateInput(assignmentFormModel.fieldNames.deadline)"
                  >
                    <ng-template #labelTpl>
                      <span class="assignment-overview-sidebar-label">
                        {{ "ASSIGNMENT_INFO.DEADLINE" | translate }}
                      </span>
                    </ng-template>
                    <ng-template #contentTpl>
                      <span class="assignment-overview-sidebar-value">
                        {{ assignment.deadline | date: uaDateFormat }}
                      </span>
                    </ng-template>
                  </app-dynamic-editable-field>
                } @else {
                  <span class="assignment-overview-sidebar-label">
                    {{ "ASSIGNMENT_INFO.DEADLINE" | translate }}
                  </span>
                  <span class="assignment-overview-sidebar-value">
                    {{ assignment.deadline | date: uaDateFormat }}
                  </span>
                }
              </div>

              @if (assignment.timeLeft) {
                <div class="assignment-overview-sidebar-item">
                  <span class="assignment-overview-sidebar-label">
                    {{ "ASSIGNMENT_INFO.TIME_LEFT" | translate }}
                  </span>
                  <span class="assignment-overview-time-left">
                    {{ assignment.timeLeft }}
                  </span>
                </div>
              }
            </div>
          </app-card-shell>

          <app-card-shell class="assignment-overview-sidebar-group" padding="md">
            <div class="assignment-overview-sidebar-list">
              @if (assignment.producer) {
                <div class="assignment-overview-sidebar-item">
                  <span class="assignment-overview-sidebar-label">
                    {{ "INPUTS.ASSIGNMENT_PRODUCER" | translate }}
                  </span>
                  <app-minimal-profile-info
                    [profile]="assignmentProducer$ | async"
                    [fullNameClass]="'assignment-overview-profile-name'"
                    imageWidth="34px"
                    imageHeight="34px"
                    fontSize="12px"
                    [collapseDetailsOnMobile]="false"
                  />
                </div>
              }

              <div class="assignment-overview-sidebar-item">
                <span class="assignment-overview-sidebar-label">
                  {{ "INPUTS.PERFORMER" | translate }}
                </span>

                @if (
                  !assignmentEmployee &&
                  (canEditAssignment$ | async) === true &&
                  !assignment.isArchived
                ) {
                  <app-search
                    [placeholderText]="'INPUTS.PERFORMER' | translate"
                    [items]="(projectEmployees$ | async)!"
                    [filterFn]="employeeSearchConfig.filterFn"
                    [compareFn]="employeeSearchConfig.compareFn"
                    [formatter]="employeeSearchConfig.formatter"
                    [customClass]="'form-search-input'"
                    (selectedItem)="onSelectEmployee($event)"
                  ></app-search>
                } @else {
                  <div class="assignment-overview-performer-row">
                    <app-minimal-profile-info
                      [profile]="assignmentEmployee"
                      [fullNameClass]="'assignment-overview-profile-name'"
                      imageWidth="34px"
                      imageHeight="34px"
                      fontSize="12px"
                      [collapseDetailsOnMobile]="false"
                    />

                    @if (
                      assignmentEmployee &&
                      (canEditAssignment$ | async) === true &&
                      !assignment.isArchived
                    ) {
                      <button
                        type="button"
                        class="assignment-overview-remove-performer"
                        (click)="removeEmployee(assignmentEmployee)"
                      >
                        ×
                      </button>
                    }
                  </div>
                }
              </div>
            </div>
          </app-card-shell>

          @if (
            !assignment.isArchived ||
            (canEditAssignment$ | async) === true ||
            (canDeleteAssignment$ | async) === true
          ) {
            <div class="assignment-overview-sidebar-actions">
              @if (!assignment.isArchived) {
                <button
                  type="button"
                  class="assignment-overview-btn assignment-overview-btn-follow"
                  (click)="isFollowing ? unfollow() : follow()"
                >
                  <span class="assignment-overview-btn-follow__icon">
                    @if (isFollowing) {
                      <svg viewBox="0 0 27 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M13.4528 15.1837C11.0855 15.1837 9.16644 13.2646 9.16644 10.8973C9.16644 8.52993 11.0855 6.61084 13.4528 6.61084C15.8202 6.61084 17.7393 8.52993 17.7393 10.8973C17.7393 13.2646 15.8202 15.1837 13.4528 15.1837Z"
                          fill="currentColor"
                        />
                        <path
                          d="M1.3743 8.13118C3.03636 5.4243 6.81699 0.882812 13.4524 0.882812C20.0878 0.882812 23.8684 5.4243 25.5305 8.13118C26.5784 9.82614 26.5784 11.9678 25.5305 13.6628C23.8684 16.3697 20.0878 20.9112 13.4524 20.9112C6.81699 20.9112 3.03636 16.3697 1.3743 13.6628C0.32637 11.9678 0.32637 9.82614 1.3743 8.13118ZM13.4524 17.3266C17.0033 17.3266 19.882 14.4479 19.882 10.897C19.882 7.34601 17.0033 4.46734 13.4524 4.46734C9.90141 4.46734 7.02274 7.34601 7.02274 10.897C7.02631 14.4465 9.90287 17.3231 13.4524 17.3266Z"
                          fill="currentColor"
                        />
                      </svg>
                    } @else {
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#assignment-overview-follow-icon)">
                          <path
                            d="M23.2711 9.41903C21.7201 6.89303 18.1921 2.65503 12.0001 2.65503C5.80805 2.65503 2.28005 6.89303 0.729051 9.41903C0.249579 10.1945 -0.00439453 11.0883 -0.00439453 12C-0.00439453 12.9118 0.249579 13.8055 0.729051 14.581C2.28005 17.107 5.80805 21.345 12.0001 21.345C18.1921 21.345 21.7201 17.107 23.2711 14.581C23.7505 13.8055 24.0045 12.9118 24.0045 12C24.0045 11.0883 23.7505 10.1945 23.2711 9.41903ZM21.5661 13.534C20.2341 15.7 17.2191 19.345 12.0001 19.345C6.78105 19.345 3.76605 15.7 2.43405 13.534C2.14919 13.0731 1.9983 12.5419 1.9983 12C1.9983 11.4582 2.14919 10.927 2.43405 10.466C3.76605 8.30003 6.78105 4.65503 12.0001 4.65503C17.2191 4.65503 20.2341 8.29603 21.5661 10.466C21.8509 10.927 22.0018 11.4582 22.0018 12C22.0018 12.5419 21.8509 13.0731 21.5661 13.534Z"
                            fill="currentColor"
                          />
                          <path
                            d="M12 7C11.0111 7 10.0444 7.29324 9.22215 7.84265C8.39991 8.39206 7.75904 9.17295 7.3806 10.0866C7.00217 11.0002 6.90315 12.0055 7.09608 12.9755C7.289 13.9454 7.76521 14.8363 8.46447 15.5355C9.16373 16.2348 10.0546 16.711 11.0246 16.9039C11.9945 17.0969 12.9998 16.9978 13.9134 16.6194C14.827 16.241 15.6079 15.6001 16.1573 14.7779C16.7068 13.9556 17 12.9889 17 12C16.9984 10.6744 16.4711 9.40356 15.5338 8.46622C14.5964 7.52888 13.3256 7.00159 12 7ZM12 15C11.4067 15 10.8266 14.8241 10.3333 14.4944C9.83994 14.1648 9.45543 13.6962 9.22836 13.1481C9.0013 12.5999 8.94189 11.9967 9.05765 11.4147C9.1734 10.8328 9.45912 10.2982 9.87868 9.87868C10.2982 9.45912 10.8328 9.1734 11.4147 9.05764C11.9967 8.94189 12.5999 9.0013 13.1481 9.22836C13.6962 9.45542 14.1648 9.83994 14.4944 10.3333C14.8241 10.8266 15 11.4067 15 12C15 12.7957 14.6839 13.5587 14.1213 14.1213C13.5587 14.6839 12.7957 15 12 15Z"
                            fill="currentColor"
                          />
                        </g>
                        <defs>
                          <clipPath id="assignment-overview-follow-icon">
                            <rect width="24" height="24" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    }
                  </span>
                  <span>
                    {{
                      (isFollowing
                        ? "COMPONENTS.ASSIGNMENT_OVERVIEW.UNFOLLOW"
                        : "COMPONENTS.ASSIGNMENT_OVERVIEW.FOLLOW") | translate
                    }}
                  </span>
                </button>
              }

              @if ((canEditAssignment$ | async) === true || (canDeleteAssignment$ | async) === true) {
                @if ((canEditAssignment$ | async) === true) {
                  @if (!assignment.isArchived) {
                    <button
                      type="button"
                      class="assignment-overview-btn assignment-overview-btn-secondary"
                      (click)="archiveAssignment(assignment.id)"
                    >
                      {{ "BUTTONS.ARCHIVE" | translate }}
                    </button>
                  } @else {
                    <button
                      type="button"
                      class="assignment-overview-btn assignment-overview-btn-secondary"
                      (click)="unarchiveAssignment(assignment.id)"
                    >
                      {{ "BUTTONS.UNARCHIVE" | translate }}
                    </button>
                  }
                }

                @if ((canDeleteAssignment$ | async) === true) {
                  <button
                    type="button"
                    class="assignment-overview-btn assignment-overview-btn-danger"
                    (click)="deleteAssignment()"
                  >
                    {{ "BUTTONS.DELETE_ASSIGNMENT" | translate }}
                  </button>
                }
              }
            </div>
          }
        </aside>
      </form>

    </div>
  </div>
}
